import { useState } from 'react';
import { Button } from '@startuptools/ui/Button';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { StripeCurrencyFormat } from '@startuptools/ui/StripeCurrencyFormat';
import { Modal } from '@startuptools/ui/Modal';
import { Typography } from '@startuptools/ui/Typography';
import { Table } from '@startuptools/ui/Table';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Input } from '@startuptools/ui/Input';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import {
  type GqlStripeAcceptQuoteMutation,
  type GqlStripeQuoteQuery,
  useStripeAcceptQuoteMutation,
  useStripePromoCodeLazyQuery,
  useStripeQuoteQuery,
  useStripeQuoteSetPromoMutation,
} from '../../graphql/react-operations';
import { isNil } from 'lodash-es';
import { LoadingDialog } from '../simple-dialogs/LoadingDialog/LoadingDialog';
import { type ApolloError } from '@apollo/client';
import { Trans, useLingui } from '@lingui/react';

export const QuoteDialog = ({
  quoteId,
  companyId,
  onCancel,
  onAccept,
}: {
  quoteId: string;
  companyId: string;
  onCancel: () => void;
  onAccept: (invoice: GqlStripeAcceptQuoteMutation['stripeAcceptQuote']) => void;
}) => {
  const quoteQuery = useStripeQuoteQuery({ variables: { companyId, quoteId } });
  const [getPromo, { error: getPromoError, loading: getPromoLoading }] = useStripePromoCodeLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [setPromo, setPromoState] = useStripeQuoteSetPromoMutation({ fetchPolicy: 'no-cache' });
  // eslint-disable-next-line sut/loading-mutation
  const [acceptQuote, acceptQuoteState] = useStripeAcceptQuoteMutation({ fetchPolicy: 'no-cache' });

  if (quoteQuery.loading || isNil(quoteQuery.data)) {
    return <LoadingDialog />;
  }
  const quote = quoteQuery.data.stripeQuote;

  const handleAccept = async () => {
    const { data, errors } = await acceptQuote({ variables: { companyId, quoteId: quote.id } });
    if (errors || !data) {
      return;
    }
    onAccept(data.stripeAcceptQuote);
  };

  const handleSetPromo = async (promoName: string) => {
    const getPromoState = await getPromo({ variables: { code: promoName.trim() } });
    if (getPromoState.error) {
      return;
    }
    const promo = getPromoState.data!.stripePromoCode;
    const { errors } = await setPromo({
      variables: { companyId, input: { quoteId: quote.id, couponId: promo.coupon.id } },
    });
    if (errors?.length) {
      return;
    }
    await quoteQuery.refetch({ companyId, quoteId: quote.id });
  };

  const loading = quoteQuery.loading || acceptQuoteState?.loading || getPromoLoading || setPromoState?.loading;

  return (
    <QuoteDialogInner
      quote={quote}
      loading={loading}
      handleAccept={handleAccept}
      handleSetPromo={handleSetPromo}
      onCancel={onCancel}
      getPromoError={getPromoError}
    />
  );
};

export const QuoteDialogInner = ({
  quote,
  onCancel,
  handleAccept,
  handleSetPromo,
  loading,
  getPromoError,
}: {
  quote: GqlStripeQuoteQuery['stripeQuote'];
  onCancel: () => void;
  handleAccept: () => Promise<void>;
  handleSetPromo: (promoName: string) => Promise<void>;
  loading: boolean;
  getPromoError: ApolloError | undefined;
}) => {
  const { i18n } = useLingui();
  const [promoName, setPromoName] = useState('');

  return (
    <Modal open onClose={onCancel}>
      <ModalDialog>
        <ModalClose />
        <Typography level="h2">
          <Trans id="Quote" />
        </Typography>
        <Stack margin={3} gap={2}>
          <Table sx={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>
                  <Trans id="Description" />
                </th>
                <th align="right">
                  <Trans id="Units" />
                </th>
                <th align="right">
                  <Trans id="Unit Price" />
                </th>
                <th align="right">
                  <Trans id="Amount" />
                </th>
              </tr>
            </thead>
            <tbody>
              {quote.line_items?.data.map(li => {
                return (
                  <tr key={li.id}>
                    <td>{li.description}</td>
                    <td align="right">{li.quantity}</td>
                    <td align="right">
                      {li.price?.unit_amount && (
                        <StripeCurrencyFormat locale={i18n.locale} currency={li.currency}>
                          {li.price?.unit_amount}
                        </StripeCurrencyFormat>
                      )}
                    </td>
                    <td align="right">
                      <StripeCurrencyFormat locale={i18n.locale} currency={li.currency}>
                        {li.amount_subtotal}
                      </StripeCurrencyFormat>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td rowSpan={5}></td>
                <td colSpan={2}>
                  <Trans id="Subtotal" />
                </td>
                <td align="right">
                  <StripeCurrencyFormat locale={i18n.locale} currency={quote.currency || 'SEK'}>
                    {quote.amount_subtotal}
                  </StripeCurrencyFormat>
                </td>
              </tr>
              {quote.total_details.amount_discount > 0 && (
                <tr>
                  <td colSpan={2}>
                    <Trans id="Discount" />
                  </td>
                  <td align="right">
                    <StripeCurrencyFormat locale={i18n.locale} currency={quote.currency || 'SEK'}>
                      {quote.total_details.amount_discount}
                    </StripeCurrencyFormat>
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={2}>
                  <Trans id="V.A.T" /> (25%)
                </td>
                <td align="right">
                  <StripeCurrencyFormat locale={i18n.locale} currency={quote.currency || 'SEK'}>
                    {quote.total_details.amount_tax}
                  </StripeCurrencyFormat>
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <b>
                    <Trans id="Total sum to be paid now" />
                  </b>
                </td>
                <td align="right">
                  <b>
                    <StripeCurrencyFormat locale={i18n.locale} currency={quote.currency || 'SEK'}>
                      {quote.computed.upfront.amount_total}
                    </StripeCurrencyFormat>
                  </b>
                </td>
              </tr>
              {!isNil(quote.computed?.recurring?.amount_total) && (
                <tr>
                  <td colSpan={2}>
                    <i>
                      <Trans id="Recurring sum" />
                    </i>
                  </td>
                  <td align="right">
                    <i>
                      <StripeCurrencyFormat
                        locale={i18n.locale}
                        currency={quote.currency || 'SEK'}
                        minimumFractionDigits={2}
                      >
                        {quote.computed.recurring.amount_total}
                      </StripeCurrencyFormat>
                    </i>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <Stack direction="row" gap={1} alignItems="start">
            <FormControl error={!isNil(getPromoError)}>
              <Input
                placeholder={i18n._({ id: 'Promo Code' })}
                value={promoName}
                onChange={event => setPromoName(event.currentTarget.value)}
              />
              {!isNil(getPromoError) && (
                <FormHelperText>
                  <Trans id="Invalid Promo Code" />
                </FormHelperText>
              )}
            </FormControl>
            <Box>
              <Button variant="outlined" onClick={() => handleSetPromo(promoName)} loading={loading}>
                <Trans id="Apply Discount" />
              </Button>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} justifyContent="end">
            <Button disabled={loading} onClick={onCancel} variant="plain">
              <Trans id="Cancel" />
            </Button>
            <Button onClick={() => handleAccept()} loading={loading}>
              <Trans id="Pay" />
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
